import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { DateRange } from '@angular/material/datepicker';
import { map, Subject, tap } from 'rxjs';
import {
  CalendarRangePickerComponent,
  createActionState,
  formValueMap,
  minDateValidator,
  removeTimezoneOffset,
} from '@at/shared';
import { DatesApi } from '../../../api/dates.api';
import { DatesService } from '../../../services/dates.service';
import { endOfDay } from 'date-fns';
import { RouterLink } from '@angular/router';
import { AvailabilityMapComponent } from '../availability-responses/availability-map.component';

@Component({
  selector: 'at-fixed-dates-selector',
  standalone: true,
  imports: [
    CalendarRangePickerComponent,
    ReactiveFormsModule,
    AsyncPipe,
    RouterLink,
    AvailabilityMapComponent,
  ],
  templateUrl: './fixed-dates-selector.component.html',
  styleUrl: './fixed-dates-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FixedDatesSelectorComponent {
  #datesApi = inject(DatesApi);
  #datesService = inject(DatesService);

  protected saveDates = new Subject<boolean>();
  protected minStartDate = signal<Date>(new Date());
  protected datesForm = new FormGroup({
    start: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: [Validators.required, minDateValidator(this.minStartDate())],
    }),
    end: new FormControl<Date | null>(null),
    notifyMembers: new FormControl<boolean>(false, { nonNullable: true }),
  });
  protected initialDates = computed<DateRange<Date> | null>(() => {
    const summary = this.#datesService.datesSummary();
    if (!summary?.data) return null;

    return summary.data.selectedDates
      ? new DateRange(
          removeTimezoneOffset(summary.data.selectedDates.start),
          removeTimezoneOffset(summary.data.selectedDates.end),
        )
      : null;
  });
  protected isEditing = computed(() => {
    const summary = this.#datesService.datesSummary();
    return summary?.data?.state === 'GatheringRSVP';
  });
  protected hasRangeData = computed(() => {
    return this.#datesService.datesSummary()?.data?.rangeCriteria;
  });
  protected moduleRootPath = this.#datesService.getModuleRootPath();

  protected readonly state = createActionState({
    trigger: this.saveDates.pipe(
      tap((notify) => this.datesForm.patchValue({ notifyMembers: notify })),
      formValueMap(this.datesForm),
      map((formValue) => ({
        ...formValue,
        start: formValue.start!,
        end: formValue.end ? endOfDay(formValue.end) : endOfDay(formValue.start!),
      })),
    ),
    beforeStart: () => {
      this.datesForm.disable();
    },
    apiCall: (request) => this.#datesApi.postDates(request),
    onError: () => {
      this.datesForm.enable();
    },
    onSucceed: () => {
      this.datesForm.enable();
      this.#datesService.updateDatesSummary(true);
    },
  });

  constructor() {
    effect(() => {
      const summary = this.#datesService.datesSummary();
      const dates = summary?.data?.selectedDates;
      if (dates) {
        this.datesForm.patchValue(dates);
      }
    });
  }
}
